import React, { useEffect, useState } from 'react';
import api from '../api';
import { useLanguage } from '../context/LanguageContext';
import { AnalyticsPreferencesButton } from '../components/Analytics';

interface LegalDocumentPageProps {
  slug: string;
  fallbackTitle: string;
  fallbackFile: string;
}

/** Scope Word-exported global styles (a:link, ...) to the document container. */
function scopeGlobalStyles(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('style').forEach((style) => {
    if (style.textContent) {
      style.textContent = style.textContent
        .replace(/a:link/gi, '.html-document-container a')
        .replace(/a:visited/gi, '.html-document-container a')
        .replace(/a:active/gi, '.html-document-container a')
        .replace(/a:hover/gi, '.html-document-container a:hover');
    }
  });
  return doc.documentElement.innerHTML;
}

/**
 * Renders a legal/informational document. Content is loaded from the API
 * (`GET /documents/{slug}`) for the active language; if the API document has
 * no content yet, it falls back to the bundled static HTML file so the public
 * site keeps working before/without migration.
 */
export default function LegalDocumentPage({
  slug,
  fallbackTitle,
  fallbackFile,
}: LegalDocumentPageProps) {
  const { language } = useLanguage();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState(fallbackTitle);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadingText = language === 'kk' ? 'Құжат жүктелуде...' : 'Загрузка документа...';
  const errorText =
    language === 'kk' ? 'Құжатты жүктеу мүмкін болмады' : 'Не удалось загрузить документ';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    const loadFallback = () =>
      fetch(fallbackFile)
        .then((r) => {
          if (!r.ok) throw new Error('not_found');
          return r.text();
        })
        .then((html) => {
          if (!active) return;
          setContent(scopeGlobalStyles(html));
          setLoading(false);
        })
        .catch(() => {
          if (!active) return;
          setError(errorText);
          setLoading(false);
        });

    api
      .get(`/documents/${slug}`, { params: { lang: language } })
      .then((res) => {
        if (!active) return;
        const html: string = res.data?.content_html || '';
        if (html.trim()) {
          setContent(scopeGlobalStyles(html));
          if (res.data?.title) setTitle(res.data.title);
          setUpdatedAt(res.data?.updated_at || null);
          setLoading(false);
        } else {
          loadFallback();
        }
      })
      .catch(() => loadFallback());

    return () => {
      active = false;
    };
  }, [slug, language, fallbackFile, errorText]);

  const updatedLabel = language === 'kk' ? 'Соңғы жаңарту' : 'Последнее обновление';

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 sm:px-8 py-8 sm:py-10 border-b border-gray-100 bg-white">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              {title}
            </h1>
            {updatedAt && (
              <p className="mt-2 text-sm text-gray-500">
                {updatedLabel}:{' '}
                {new Date(updatedAt).toLocaleDateString(language === 'kk' ? 'kk-KZ' : 'ru-RU')}
              </p>
            )}
          </div>

          <div className="p-6 sm:p-8 text-gray-700 leading-relaxed">
            {slug === 'privacy' && <section className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <h2 className="font-semibold text-slate-900">{language === 'kk' ? 'Қолдау қызметіне жүгіну' : 'Обращения в поддержку'}</h2>
              <p className="mt-3 text-sm">{language === 'kk'
                ? 'Чатты өзіңіз ашқаннан кейін қолдау жүйесіне қосылу басталады. Хат алмасуды жалғастыру үшін браузерде техникалық идентификатор сақталады. Тіркелгіге кіргенде қолдау қызметіне атыңыз, email мекенжайыңыз және тіркелгі идентификаторы беріледі. Операторға сайт бөлімі мен интерфейс тілі көрсетіледі; іздеу сұраулары мен қаржылық есептеулер автоматты түрде жіберілмейді.'
                : 'Подключение к системе поддержки начинается после открытия чата. Для продолжения переписки в браузере сохраняется технический идентификатор. При входе в аккаунт в поддержку передаются имя, email и идентификатор аккаунта. Оператор видит раздел сайта и язык интерфейса; поисковые запросы и финансовые расчёты автоматически не передаются.'}</p>
              <p className="mt-3 text-sm">{language === 'kk'
                ? 'Өтініштер мен хат алмасуға қолдау қызметінің уәкілетті қызметкерлері қол жеткізе алады. Хабарламалар Google Analytics пен Яндекс Метрикаға жіберілмейді. Аналитикадан бас тарту қолдау қызметінің жұмысына әсер етпейді. Құпиясөздер мен банк карталарының деректерін жібермеңіз. Деректеріңізге қатысты сұрақтарды осы сайттың байланыс нысаны арқылы жолдай аласыз.'
                : 'Обращения и переписка доступны уполномоченным сотрудникам поддержки. Тексты сообщений не отправляются в Google Analytics и Яндекс Метрику. Отказ от аналитики не влияет на работу поддержки. Не отправляйте пароли и данные банковских карт. Запросы о ваших данных можно направить через форму контактов на этом сайте.'}</p>
            </section>}
            {slug === 'privacy' && <section className="mb-8 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <h2 className="font-semibold text-slate-900">{language === 'kk' ? 'Сайт аналитикасы' : 'Аналитика сайта'}</h2>
              <p className="mt-3 text-sm">{language === 'kk' ? 'Сіздің таңдауыңызбен Google Analytics және Яндекс Метрика сайтқа өтулерді және қызметтерді пайдалану әрекеттерін өлшейді. Құпиясөздер, PDF құжаттарының мазмұны мен қаржылық есептеу мәндері аналитикаға жіберілмейді. Сеанстарды бейнежазу өшірілген. Аналитикадан бас тарту негізгі қызметтерге қолжетімділікті өзгертпейді.' : 'С вашего согласия Google Analytics и Яндекс Метрика измеряют переходы на сайт и действия при использовании услуг. Пароли, содержимое PDF и значения финансовых расчётов в аналитику не передаются. Запись сеансов отключена. Отказ от аналитики не влияет на доступ к основным услугам.'}</p>
              <p className="mt-3 text-sm">{language === 'kk' ? 'Таңдауыңызды төмендегі батырма арқылы өзгерте аласыз.' : 'Изменить выбор можно кнопкой ниже. Аналитические сервисы используют собственные идентификаторы браузера и правила обработки данных.'}</p>
              <div className="mt-3"><AnalyticsPreferencesButton /></div>
              <p className="mt-3 flex flex-wrap gap-4 text-sm"><a href="https://policies.google.com/privacy?hl=ru" target="_blank" rel="noopener noreferrer" className="underline">Google</a><a href="https://yandex.ru/legal/confidential/" target="_blank" rel="noopener noreferrer" className="underline">Яндекс</a></p>
            </section>}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-goszakup-600" />
                <span className="ml-3 text-gray-500">{loadingText}</span>
              </div>
            ) : error ? (
              <div className="text-center p-12 border-2 border-dashed border-red-300 rounded-xl bg-red-50 text-red-600">
                {error}
              </div>
            ) : (
              <div className="html-document-container" dangerouslySetInnerHTML={{ __html: content }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
